import React, { useEffect, useState, useCallback, useMemo } from 'react';
import InboxIcon from '@mui/icons-material/Inbox';
import { OrderApi } from '../../api/orderApi';
import useTranslation from '../../hooks/useTranslation';

const withCount = (label, n) => (typeof n === 'number' ? `${label} (${n})` : label);

export default function useOrdersMenu() {
  const { t } = useTranslation();
  const [counts, setCounts] = useState({});

  const readSelectedStoreId = useCallback(() => {
    try {
      const raw = localStorage.getItem('selectedStore');
      return raw ? (JSON.parse(raw)?.id ?? null) : null;
    } catch {
      return null;
    }
  }, []);

  const fetchCounts = useCallback(async (storeId) => {
    try {
      const params = storeId ? { store_id: storeId } : {};
      const { data } = await OrderApi.getOrderStats(params);
      setCounts(data || {});
    } catch (e) {
      console.error('failed', e);
      setCounts({});
    }
  }, []);

  useEffect(() => {
    fetchCounts(readSelectedStoreId());

    const onStoreChanged = (e) => {
      const storeId = e.detail?.storeId || null;
      fetchCounts(storeId);
    };

    window.addEventListener('orders:storeChanged', onStoreChanged);
    return () => window.removeEventListener('orders:storeChanged', onStoreChanged);
  }, [fetchCounts, readSelectedStoreId]);

  const items = useMemo(() => ([
    {
      title: withCount('All Orders', counts.all),
      to: '/admin/orders/all',
      extraLinks: ['/admin/orders/all'],
      active: false
    },
    {
      title: withCount(t('Fulfilled Orders'), counts.fulfilled),
      to: '/admin/orders/fulfilled',
      extraLinks: ['/admin/orders/fulfilled'],
      active: false
    },
    {
      title: withCount(t('Unfulfilled Orders'), counts.unfulfilled),
      to: '/admin/orders/unfulfilled',
      extraLinks: ['/admin/orders/unfulfilled'],
      active: false
    },
    {
      title: withCount(t('Paid Orders'), counts.paid),
      to: '/admin/orders/paid',
      extraLinks: ['/admin/orders/paid'],
      active: false
    },
    {
      title: withCount(t('Unpaid Orders'), counts.unpaid),
      to: '/admin/orders/unpaid',
      extraLinks: ['/admin/orders/unpaid'],
      active: false
    }
  ]), [counts, t]);

  return useMemo(() => ({
    icon: <InboxIcon />,
    title: t('labels.menu.orders'),
    open: false,
    items
  }), [items, t]);
}
